import { useState } from "react";

import { accountHint, cohortPreview, shortFunder, type TargetChoice } from "../lib/accounts";
import { useAccounts, useCommandTarget, useFormTarget } from "../store/useAccounts";
import { useSettings } from "../store/useSettings";
import { CohortEditor } from "./CohortEditor";

/**
 * Выбор аккаунта или когорты.
 *
 * Два применения, и цели у них РАЗНЫЕ (замечание владельца 2026-10-07):
 *
 *   - `form` — над формой «Новый ордер», и действует только на неё;
 *   - `active` — активная строка быстрых действий: клавиши, сигналы, тир. На
 *     странице рынка её выбирают щелчком по строке панели, а этот селектор
 *     стоит там, где строк нет (страница возврата).
 *
 * Кнопки быстрых действий не смотрят ни на тот, ни на другой: у каждой
 * строки панели цель своя.
 *
 * # Почему нет выбора по умолчанию
 *
 * Инвариант 4 roadmap-accounts: даже при одном аккаунте он не подставляется
 * сам. Второй аккаунт, появившись в наборе по SIGHUP, иначе молча унаследовал
 * бы нажатия, адресованные первому. Выбор запоминается в настройках вкладки,
 * а в запросе едет явно.
 */
export function AccountPicker({ scope = "active" }: { scope?: "form" | "active" }) {
  const accounts = useAccounts((s) => s.data);
  const cohorts = useAccounts((s) => s.cohorts);
  const error = useAccounts((s) => s.error);
  const form = scope === "form";
  const choice = useSettings((s) => (form ? s.settings.formTarget : s.settings.target));
  const setTarget = useSettings((s) => (form ? s.setFormTarget : s.setTarget));
  // Оба хука вызываются всегда — порядок хуков не зависит от пропа.
  const formCommand = useFormTarget();
  const activeCommand = useCommandTarget();
  const command = form ? formCommand : activeCommand;
  const [editing, setEditing] = useState(false);

  const rows = accounts?.accounts ?? [];
  const list = cohorts ?? [];
  // Значение select'а несёт вид цели: имя когорты и адрес не пересекаются по
  // форме, но явный префикс не даёт этому стать допущением.
  const value = choice ? (choice.kind === "account" ? `a:${choice.funder}` : `c:${choice.name}`) : "";
  // Выбор, выпавший из состава или удалённый, остаётся в списке отдельной
  // строкой: молча показать «не выбрано» значило бы скрыть, что выбор был и
  // пропал.
  const orphan =
    choice?.kind === "account" && !rows.some((a) => a.funder === choice.funder) ? choice.funder : null;
  const goneCohort =
    choice?.kind === "cohort" && cohorts && !list.some((c) => c.name === choice.name) ? choice.name : null;
  const row = choice?.kind === "account" ? (rows.find((a) => a.funder === choice.funder) ?? null) : null;
  const cohort = choice?.kind === "cohort" ? (list.find((c) => c.name === choice.name) ?? null) : null;
  const preview = cohort ? cohortPreview(cohort, accounts) : null;
  const hint =
    command.problem ??
    (row ? accountHint(row) : null) ??
    (preview && preview.eligible.length === 0 ? "в когорте нет аккаунтов для постановки" : null);

  function pick(raw: string) {
    let next: TargetChoice | null = null;
    if (raw.startsWith("a:")) next = { kind: "account", funder: raw.slice(2) };
    if (raw.startsWith("c:")) next = { kind: "cohort", name: raw.slice(2) };
    setTarget(next);
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <select
        value={value}
        onChange={(e) => pick(e.target.value)}
        // Ширина ограничена: без неё селект растягивается по самой длинной
        // строке списка («B01 — не загружен трейдером, осушается») и съедает
        // место подсказки, которая и объясняет, почему кнопка не жмётся.
        className="max-w-56 min-w-0 shrink rounded border bg-transparent px-1.5 py-1 text-xs font-medium"
        style={{
          borderColor: command.target ? "var(--color-border)" : "var(--color-warn, #fbbf24)",
          color: command.target ? "var(--color-fg)" : "var(--color-warn, #fbbf24)",
        }}
        title={
          accounts && !accounts.known
            ? `состав аккаунтов неизвестен: ${accounts.reason ?? "нет sidecar'а и трейдер молчит"}`
            : form
              ? "от чьего имени уходит ордер этой формы — и только он"
              : "активная строка: от её имени клавиши, взятие сигналов и тир"
        }
      >
        <option value="">аккаунт…</option>
        <optgroup label="аккаунты">
          {rows.map((a) => (
            <option key={a.funder} value={`a:${a.funder}`}>
              {a.slot}
              {accountHint(a) ? ` — ${accountHint(a)}` : ""}
            </option>
          ))}
          {orphan && <option value={`a:${orphan}`}>{shortFunder(orphan)} — вне состава</option>}
        </optgroup>
        {/* Когорты — отдельной группой: ордер когортой ставит каждый аккаунт
            полным размером, и спутать её с аккаунтом значит умножить риск. */}
        {(list.length > 0 || goneCohort) && (
          <optgroup label="когорты — каждый полным размером">
            {list.map((c) => (
              <option key={c.name} value={`c:${c.name}`}>
                {c.name} ({cohortPreview(c, accounts).text})
              </option>
            ))}
            {goneCohort && <option value={`c:${goneCohort}`}>{goneCohort} — удалена</option>}
          </optgroup>
        )}
      </select>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="shrink-0 rounded border border-[var(--color-border)] px-1.5 py-1 text-xs text-[var(--color-muted)] hover:text-white"
        title="завести или поправить когорты аккаунтов"
      >
        когорты
      </button>
      {preview && !hint && (
        <span
          className="min-w-0 truncate text-xs text-amber-300"
          title={
            preview.skipped.length > 0
              ? "пропущены: " + preview.skipped.map((s) => `${s.label} (${s.reason})`).join(", ")
              : undefined
          }
        >
          {preview.text}
          {preview.skipped.length > 0 ? `, пропущено ${preview.skipped.length}` : ""}
        </span>
      )}
      {hint && (
        <span className="min-w-0 truncate text-xs text-amber-300" title={hint}>
          {hint}
        </span>
      )}
      {!hint && !preview && error && (
        <span className="min-w-0 truncate text-xs text-[var(--color-muted)]" title={error}>
          состав не обновился: {error}
        </span>
      )}
      {editing && <CohortEditor onClose={() => setEditing(false)} />}
    </div>
  );
}
